/**
 * Owner: Masa Bayu
 * File: publication-lifecycle.ts
 * Date:Time: 2026-09-15 20:19
 * Description: T1 catalog publication lifecycle states (≠ QXP mutation).
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { assertTimestamp, assertValidId } from './guards';
import { createQxreadPublicationRef, } from './publication-ref';
/** Marketplace listing lifecycle — independent of QXP internal truth. */
export const QXREAD_LISTING_STATES = [
    'draft_listing',
    'live',
    'updated',
    'unavailable',
    'retired',
];
export const QXREAD_AVAILABILITY_STATES = [
    'available',
    'preview_only',
    'coming_soon',
    'unavailable',
];
function assertListingState(value, label) {
    if (typeof value !== 'string' || !QXREAD_LISTING_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_LISTING_STATES.join(', ')}`);
    }
    return value;
}
function assertAvailability(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_AVAILABILITY_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_AVAILABILITY_STATES.join(', ')}`);
    }
    return value;
}
export function createQxreadPublicationLifecycle(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead publication lifecycle');
    assertValidId(options.publicationId, 'QXRead publicationId');
    const updatedAt = options.updatedAt ?? new Date().toISOString();
    assertTimestamp(updatedAt, 'QXRead lifecycle updatedAt');
    const listingState = assertListingState(options.listingState ?? 'draft_listing', 'QXRead listingState');
    const availability = assertAvailability(options.availability ?? 'coming_soon', 'QXRead availability');
    if (listingState === 'live' && availability === 'unavailable') {
        throw new Error('QXRead live listing cannot be availability unavailable');
    }
    if (listingState === 'draft_listing' && availability === 'available') {
        throw new Error('QXRead draft listing cannot be fully available');
    }
    return Object.freeze({
        publicationId: options.publicationId,
        listingState,
        availability,
        updatedAt,
    });
}
/** Advance listing toward marketplace readiness without mutating QXP. */
export function transitionListingState(current, next, at) {
    const allowed = {
        draft_listing: ['live', 'retired'],
        live: ['updated', 'unavailable', 'retired'],
        updated: ['live', 'unavailable', 'retired'],
        unavailable: ['live', 'retired'],
        retired: [],
    };
    if (!allowed[current.listingState].includes(next)) {
        throw new Error(`QXRead listing cannot transition from "${current.listingState}" to "${next}"`);
    }
    const availability = next === 'live' || next === 'updated'
        ? 'available'
        : next === 'unavailable'
            ? 'unavailable'
            : current.availability;
    return createQxreadPublicationLifecycle({
        publicationId: current.publicationId,
        listingState: next,
        availability,
        updatedAt: at ?? new Date().toISOString(),
    });
}
export function isMarketplaceReady(input) {
    return (input.ref.state === 'bound' &&
        (input.lifecycle.listingState === 'live' || input.lifecycle.listingState === 'updated') &&
        (input.lifecycle.availability === 'available' ||
            input.lifecycle.availability === 'preview_only') &&
        (input.ref.visibility === 'public' || input.ref.visibility === 'unlisted'));
}
export function createDraftCatalogPublication(options) {
    const ref = createQxreadPublicationRef({
        title: options.title,
        visibility: options.visibility ?? 'unlisted',
        state: 'unbound',
    });
    const lifecycle = createQxreadPublicationLifecycle({
        publicationId: ref.publicationId,
        listingState: 'draft_listing',
        availability: 'coming_soon',
    });
    return Object.freeze({ ref, lifecycle });
}
//# sourceMappingURL=publication-lifecycle.js.map